import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import MindGameBrand from "../MindGameBrand";
import CorporateDialog from "./CorporateDialog";
import { createThresholdScene } from "./ThresholdScene";
import "./threshold.css";
import "./vaultAuth.css";
import "./polish.css";

export default function VaultLoginShell({ children, exiting, onExited, destinationRef, primary }) {
  const canvasHost = useRef(null);
  const fontStyles = useRef(null);
  const controller = useRef(null);
  const corporateTrigger = useRef(null);
  const [corporateView, setCorporateView] = useState(null);
  const [fontsReady, setFontsReady] = useState(false);
  const [uiRevealed, setUiRevealed] = useState(false);
  const [error, setError] = useState(null);

  useLayoutEffect(() => {
    let active = true;
    const stylesheet = fontStyles.current;
    const failed = error => {
      console.error("The vault branding font could not load:", error);
      if (active) {
        setError("The branding font could not load. Using the fallback font.");
        setFontsReady(true);
      }
    };
    const loaded = () => {
      Promise.all([document.fonts.load('500 14px "Jura"'), document.fonts.load('700 64px "Jura"')])
        .then(() => { if (active) setFontsReady(true); }).catch(failed);
    };
    stylesheet.addEventListener("load", loaded);
    stylesheet.addEventListener("error", failed);
    if (stylesheet.sheet) loaded();
    return () => {
      active = false;
      stylesheet.removeEventListener("load", loaded);
      stylesheet.removeEventListener("error", failed);
    };
  }, []);

  useEffect(() => {
    const scene = createThresholdScene(canvasHost.current, setError, destinationRef.current);
    controller.current = scene;
    return () => { scene.dispose(); controller.current = null; };
  }, [destinationRef]);

  useEffect(() => {
    if (!exiting) return undefined;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      const destination = destinationRef.current;
      destination.style.visibility = "visible";
      destination.style.opacity = "1";
      destination.style.filter = "none";
      destination.style.maskImage = "none";
      onExited();
    };
    const onVisibility = () => { if (document.visibilityState !== "visible") finish(); };
    document.addEventListener("visibilitychange", onVisibility);
    if (document.visibilityState !== "visible") finish();
    else controller.current.enter(finish);
    return () => {
      finished = true;
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [exiting, onExited, destinationRef]);

  useEffect(() => {
    if (error && exiting) {
      destinationRef.current.style.filter = "none";
      onExited();
    }
  }, [error, exiting, onExited, destinationRef]);

  return <div className="v6-vault-shell v6-production v6-polished">
    <main className={`tv2-root v6-root${exiting ? " v6-transitioning" : ""}${uiRevealed ? " v6-logo-still" : ""}${fontsReady ? " v6-ui-ready" : ""}`}
      aria-label="mindGAME sign in" aria-hidden={exiting || undefined} inert={exiting ? "" : undefined}>
      <link ref={fontStyles} rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Jura:wght@300;400;500;600;700&display=swap" />
      <div ref={canvasHost} className="tv2-scene" role="img"
        aria-label="A dark architectural threshold with a concealed coral light escaping through a narrow seam" />
      <div className="tv2-veil" aria-hidden="true" />
      <div className="tv2-registration" aria-hidden="true"><span>00 /</span><p>
        {["GAMING", "INTELLIGENCE"].map((word, line) =>
          <span className="v6-registration-line" key={word}>{Array.from(word, (letter, index) =>
            <span className="v6-registration-letter" key={index} style={{ "--type-index": [0, 6, 18, 24][line] + index }}>{letter}</span>)}</span>)}
      </p></div>
      <aside className="tv2-controls v6-chrome" aria-label="Corporate information">
        <span>03 / CORPORATE</span>
        {["terms", "privacy", "support"].map(view => <button key={view} type="button"
          disabled={exiting} aria-haspopup="dialog" onClick={event => {
            corporateTrigger.current = event.currentTarget;
            setCorporateView(view);
          }}>{view === "terms" ? "Terms" : view === "privacy" ? "Privacy" : "Support"}</button>)}
        <Link to="/API">API</Link>
      </aside>
      <section className="tv2-access" aria-labelledby="tv2-brand">
        <div className={`tv2-login${primary ? " v6-primary-login" : " v6-secondary-login"}`}
          onPointerEnter={() => controller.current?.setFocus(true)}
          onPointerLeave={event => controller.current?.setFocus(event.currentTarget.contains(document.activeElement))}
          onFocus={() => { setUiRevealed(true); controller.current?.setFocus(true); }}
          onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) controller.current?.setFocus(false); }}>
          <div id="tv2-brand" className="v6-brand" aria-label="mindGAME">
            <MindGameBrand variant="color-ring" animated={!uiRevealed && !exiting} ready={fontsReady} />
          </div>
          {children}
        </div>
      </section>
      {error && <div className="tv2-error" role="alert">{error}</div>}
      <footer className="tv2-footer v6-chrome"><span>&copy; 2026 mindGAME</span><span>02 / THE THRESHOLD</span></footer>
      {corporateView && <CorporateDialog view={corporateView} returnFocus={corporateTrigger.current} onClose={() => setCorporateView(null)} />}
    </main>
    <div className="v6-transport" aria-hidden="true" />
  </div>;
}
